import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Save, RotateCcw, Clock, AlertTriangle, Calendar, CheckSquare, Phone, Info } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SLA_CONFIG = {
  first_touch_sla: 1,
  follow_up_sla: 24,
  callback_reminder: 1,
  meeting_reminder: 24,
  inactivity_sla: 48,
  task_due_reminder: 2,
};

const SLA_FIELDS = [
  {
    key: 'first_touch_sla',
    label: 'First Touch SLA',
    description: 'Time allowed to make first contact with a new lead',
    icon: Phone,
    unit: 'hours',
    min: 0.5,
    max: 72,
    step: 0.5,
  },
  {
    key: 'follow_up_sla',
    label: 'Follow-up Window',
    description: 'Default time until next follow-up after an action',
    icon: Clock,
    unit: 'hours',
    min: 1,
    max: 168,
    step: 1,
  },
  {
    key: 'inactivity_sla',
    label: 'Inactivity Threshold',
    description: 'Time without action before lead returns to actionable',
    icon: AlertTriangle,
    unit: 'hours',
    min: 12,
    max: 336,
    step: 12,
  },
  {
    key: 'task_due_reminder',
    label: 'Task Due Reminder',
    description: 'Time before task due date to mark lead as actionable',
    icon: CheckSquare,
    unit: 'hours',
    min: 0.5,
    max: 48,
    step: 0.5,
  },
  {
    key: 'meeting_reminder',
    label: 'Meeting Reminder',
    description: 'Time before meeting to mark lead as actionable',
    icon: Calendar,
    unit: 'hours',
    min: 1,
    max: 72,
    step: 1,
  },
  {
    key: 'callback_reminder',
    label: 'Callback Reminder',
    description: 'Time before scheduled callback to mark lead as actionable',
    icon: Phone,
    unit: 'hours',
    min: 0.5,
    max: 24,
    step: 0.5,
  },
];

const SLASettings = () => {
  const [config, setConfig] = useState(DEFAULT_SLA_CONFIG);
  const [originalConfig, setOriginalConfig] = useState(DEFAULT_SLA_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    const changed = JSON.stringify(config) !== JSON.stringify(originalConfig);
    setHasChanges(changed);
  }, [config, originalConfig]);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/lead-mgmt/sla-config`, {
        params: { token }
      });
      
      if (response.data.success && response.data.config) {
        setConfig(response.data.config);
        setOriginalConfig(response.data.config);
      }
    } catch (error) {
      console.error('Error fetching SLA config:', error);
      toast.error('Failed to load SLA configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/api/lead-mgmt/sla-config?token=${token}`,
        { settings: config }
      );
      
      if (response.data.success) {
        setOriginalConfig(config);
        toast.success('SLA configuration saved successfully');
      } else {
        toast.error(response.data.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving SLA config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save SLA configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(originalConfig);
    toast.info('Changes discarded');
  };

  const handleResetToDefault = () => {
    setConfig(DEFAULT_SLA_CONFIG);
    toast.info('Reset to default values - click Save to apply');
  };

  const handleValueChange = (key, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setConfig(prev => ({ ...prev, [key]: numValue }));
    }
  };

  const formatDisplayValue = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutes`;
    } else if (hours === 1) {
      return '1 hour';
    } else if (hours < 24) {
      return `${hours} hours`;
    } else if (hours === 24) {
      return '1 day';
    } else {
      const days = hours / 24;
      return days === Math.floor(days) ? `${days} days` : `${hours} hours`;
    }
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="sla-settings-loading">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="sla-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lead SLA Configuration</h1>
          <p className="text-slate-500 mt-1">
            Configure timing thresholds for the "My Actionable Leads" workflow
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              Unsaved Changes
            </Badge>
          )}
        </div>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          These settings control when leads become "actionable" and appear in the My Actionable Leads tab.
          A background job runs every minute to check and update lead statuses based on these thresholds.
        </AlertDescription>
      </Alert>

      {/* SLA Configuration Cards */}
      <div className="grid gap-4">
        {SLA_FIELDS.map((field) => {
          const Icon = field.icon;
          const currentValue = config[field.key] ?? DEFAULT_SLA_CONFIG[field.key];
          const defaultValue = DEFAULT_SLA_CONFIG[field.key];
          const isModified = currentValue !== defaultValue;

          return (
            <Card key={field.key} className={isModified ? 'border-blue-200 bg-blue-50/30' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${isModified ? 'bg-blue-100' : 'bg-slate-100'}`}>
                    <Icon className={`h-5 w-5 ${isModified ? 'text-blue-600' : 'text-slate-600'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Label className="text-base font-medium text-slate-900">
                        {field.label}
                      </Label>
                      {isModified && (
                        <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                          Modified
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{field.description}</p>
                    
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={currentValue}
                          onChange={(e) => handleValueChange(field.key, e.target.value)}
                          min={field.min}
                          max={field.max}
                          step={field.step}
                          className="w-24"
                          data-testid={`sla-input-${field.key}`}
                        />
                        <span className="text-sm text-slate-500">{field.unit}</span>
                      </div>
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="text-xs cursor-help">
                              = {formatDisplayValue(currentValue)}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Default: {formatDisplayValue(defaultValue)}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleResetToDefault}
          className="text-slate-600"
          data-testid="reset-to-default-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges}
            data-testid="discard-changes-btn"
          >
            Discard Changes
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-sla-config-btn"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      </div>

      {/* How It Works Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">How It Works</CardTitle>
          <CardDescription>Understanding the actionable leads workflow</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-slate-600">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-800 mb-2">Entry Criteria (Becomes Actionable)</h4>
              <ul className="list-disc list-inside space-y-1 text-green-700">
                <li>New lead assigned to you</li>
                <li>Scheduled follow-up time passed</li>
                <li>Inactivity threshold exceeded</li>
                <li>Task due within reminder window</li>
                <li>Meeting within reminder window</li>
              </ul>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">Exit Criteria (Leaves Actionable)</h4>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>You make a call</li>
                <li>You add a note</li>
                <li>You send WhatsApp/Email</li>
                <li>You schedule a meeting</li>
                <li>You change lead status</li>
              </ul>
            </div>
          </div>
          
          <p className="text-slate-500 italic">
            A background job runs every 60 seconds to check all leads against these thresholds and update their actionable status automatically.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SLASettings;
